// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Grid,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  ThumbUp,
  ThumbDown
} from '@mui/icons-material';

const SentimentAnalysisCard = ({ data }) => {
  if (!data) {
    return (
      <Paper sx={{ p: 3, height: '100%' }}>
        <Typography variant="h6" gutterBottom>
          Sentiment Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Paper>
    );
  }

  const { positive, negative, positive_percentage, negative_percentage } = data;
  const total = positive + negative;

  return (
    <Paper sx={{ p: 1.5, height: '100%', width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Sentiment Analysis
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Overall sentiment distribution
      </Typography>

      <Grid container spacing={1}>
        {/* Positive Sentiment */}
        <Grid item xs={6}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
              <ThumbUp sx={{ color: 'success.main', fontSize: 24, mr: 0.5 }} />
              <Typography variant="h5" color="success.main" fontWeight="bold">
                {positive_percentage}%
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Positive
            </Typography>
            <Chip
              label={`${positive} records`}
              size="small"
              color="success"
              variant="outlined"
            />
          </Box>
        </Grid>

        {/* Negative Sentiment */}
        <Grid item xs={6}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
              <ThumbDown sx={{ color: 'error.main', fontSize: 24, mr: 0.5 }} />
              <Typography variant="h5" color="error.main" fontWeight="bold">
                {negative_percentage}%
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Negative
            </Typography>
            <Chip
              label={`${negative} records`}
              size="small"
              color="error"
              variant="outlined"
            />
          </Box>
        </Grid>
      </Grid>

      {/* Progress Bar */}
      <Box sx={{ mt: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="success.main">
            Positive
          </Typography>
          <Typography variant="body2" color="error.main">
            Negative
          </Typography>
        </Box>
        <Box sx={{ position: 'relative' }}>
          <LinearProgress
            variant="determinate"
            value={positive_percentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'error.light',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'success.main',
                borderRadius: 4
              }
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {positive} of {total} records
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total: {total} records
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default SentimentAnalysisCard;
